import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { displayReqNumber } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = [
  { id: "Incident", label: "Report Incident (device broken / not working)", priority: "High" },
  { id: "Lost or Stolen", label: "Report Lost or Stolen Device", priority: "Critical" },
  { id: "Data Wipe", label: "Request Certified Data-Wipe", priority: "Standard" },
  { id: "Device Return", label: "Return / Refurbish Device", priority: "Standard" },
  { id: "Help Desk", label: "Generic Help Desk Question", priority: "Standard" },
];

export default function IncidentDialog({ open, onClose, defaultCategory = "Incident", deviceRef = null, onSuccess }) {
  const { persona, userName, userEmail } = usePersona();
  const [category, setCategory] = useState(defaultCategory);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("High");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setCategory(defaultCategory);
      const c = CATEGORIES.find((x) => x.id === defaultCategory);
      setPriority(c?.priority || "Standard");
      setSubject(deviceRef ? `${defaultCategory} — ${deviceRef}` : "");
      setDescription("");
      setResult(null);
    }
  }, [open, defaultCategory, deviceRef]);

  const submit = async () => {
    if (!subject.trim() || !description.trim()) {
      toast.error("Please add a subject and description");
      return;
    }
    setSubmitting(true);
    try {
      const t = await endpoints.createIncident({
        persona_id: persona.id,
        user_name: userName || "Employee",
        user_email: userEmail || null,
        category,
        subject,
        description,
        device_ref: deviceRef,
        priority,
      });
      setResult(t);
      toast.success(`Incident ${displayReqNumber(t)} logged`);
      onSuccess?.(t);
    } catch { toast.error("Could not log incident"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent data-testid="incident-dialog" className="max-w-lg bg-white">
        {!result ? (
          <>
            <DialogHeader>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
                <AlertTriangle size={12} className="text-[#DA291C]" /> ITSM
              </div>
              <DialogTitle className="font-display text-2xl text-slate-900">Log a Ticket</DialogTitle>
              <DialogDescription>Creates an incident ticket routed to the IT Service Desk.</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Category</Label>
                <Select value={category} onValueChange={(v) => { setCategory(v); const c = CATEGORIES.find((x) => x.id === v); if (c) setPriority(c.priority); }}>
                  <SelectTrigger data-testid="incident-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`incident-cat-${c.id.replace(/\s+/g, '-')}`}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {deviceRef && (
                <div className="text-xs bg-slate-100 border border-slate-200 rounded-md px-3 py-2 text-slate-700">
                  Device: <span className="font-mono">{deviceRef}</span>
                </div>
              )}

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Subject</Label>
                <Input data-testid="incident-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief summary" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Description</Label>
                <Textarea data-testid="incident-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What happened? Steps to reproduce, error messages…" className="min-h-24" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger data-testid="incident-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Standard">Standard</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="incident-cancel">Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid="incident-submit" className="bg-[#0033A0] hover:bg-[#002573] text-white">
                {submitting && <Loader2 size={14} className="animate-spin mr-2" />} Log Ticket
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="text-center py-6" data-testid="incident-result">
            <div className="w-14 h-14 rounded-full bg-green-100 mx-auto grid place-items-center">
              <AlertTriangle className="text-green-600" size={24} />
            </div>
            <div className="font-display font-black text-xl text-slate-900 mt-3">Ticket Logged</div>
            <div className="mt-1 text-sm text-slate-500">Routed to IT Service Desk</div>
            <div className="mt-4 font-mono font-bold text-2xl text-[#0033A0]" data-testid="incident-req">{displayReqNumber(result)}</div>
            {result.sn_number && result.sn_number !== result.req_number && (
              <div className="mt-1 text-[10px] text-slate-400 font-mono">Local Flexspace REQ: {result.req_number}</div>
            )}
            <Button className="mt-4 bg-[#0033A0] hover:bg-[#002573] text-white" onClick={() => { onClose?.(); window.location.href = `/tickets/${result.id}`; }} data-testid="incident-view">
              View Ticket
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
